import { TypedDocumentNode, gql } from '@apollo/client';

import { isType, withFragments } from '@sorare/core/src/lib/gql';
import {
  NegativeDecisiveStatKey,
  PositiveDecisiveStatKey,
} from '@sorare/core/src/lib/scoring';
import {
  negativeDecisiveStatKeys,
  positiveDecisiveStatKeys,
} from '@sorare/core/src/lib/stats';

import { getDecisiveActions_playerGameScoreInterface } from './__generated__/lib.graphql';

export const getDecisiveActions = withFragments(
  (
    playerGameScore: getDecisiveActions_playerGameScoreInterface
  ): Record<
    PositiveDecisiveStatKey | NegativeDecisiveStatKey | 'yellow_card',
    number
  > => {
    const {
      positiveDecisiveStats = [],
      negativeDecisiveStats = [],
      anyPlayerGameStats = null,
    } = isType(playerGameScore, 'PlayerGameScore') ||
    isType(playerGameScore, 'So5Score')
      ? playerGameScore
      : {};

    const decisiveStatsScores = [
      ...positiveDecisiveStats,
      ...negativeDecisiveStats,
    ];

    const decisiveStats = [
      ...positiveDecisiveStatKeys,
      ...negativeDecisiveStatKeys,
    ].reduce<Record<PositiveDecisiveStatKey | NegativeDecisiveStatKey, number>>(
      (acc, statKey) => ({
        ...acc,
        [statKey]:
          decisiveStatsScores.find(stat => stat.stat === statKey)?.statValue ||
          0,
      }),
      {} as Record<PositiveDecisiveStatKey | NegativeDecisiveStatKey, number>
    );

    return {
      ...decisiveStats,
      yellow_card:
        (isType(anyPlayerGameStats, 'PlayerGameStats')
          ? anyPlayerGameStats.yellowCard
          : 0) || 0,
    };
  },
  {
    playerGameScoreInterface: gql`
      fragment getDecisiveActions_playerGameScoreInterface on PlayerGameScoreInterface {
        id
        ... on PlayerGameScore {
          id
          positiveDecisiveStats {
            stat
            statValue
          }
          negativeDecisiveStats {
            stat
            statValue
          }
          anyPlayerGameStats {
            id
            ... on PlayerGameStats {
              id
              yellowCard
            }
          }
        }
        ... on So5Score {
          id
          positiveDecisiveStats {
            stat
            statValue
          }
          negativeDecisiveStats {
            stat
            statValue
          }
          anyPlayerGameStats {
            id
            ... on PlayerGameStats {
              id
              yellowCard
            }
          }
        }
      }
    ` as TypedDocumentNode<getDecisiveActions_playerGameScoreInterface>,
  }
);

export const PITCHING_STAT_KEYS = [
  'outs',
  'strikeouts',
  'hitsAllowed',
  'earnedRuns',
  'walks',
  'hitBatsmen',
  'win',
  'save',
  'hold',
  'reliefAppearance',
] as const;

export type PitchingStatKey = (typeof PITCHING_STAT_KEYS)[number];

export const BATTING_STAT_KEYS = [
  'runs',
  'rbi',
  'singles',
  'doubles',
  'triples',
  'homeRuns',
  'walks',
  'strikeouts',
  'stolenBases',
  'hitByPitch',
  'stealsCaught',
] as const;

export type BattingStatKey = (typeof BATTING_STAT_KEYS)[number];

export const shortPitchingStatMessages: Record<PitchingStatKey, string> = {
  outs: 'IP',
  strikeouts: 'K',
  hitsAllowed: 'H',
  earnedRuns: 'ER',
  walks: 'BB',
  hitBatsmen: 'HB',
  win: 'W',
  save: 'SV',
  hold: 'HLD',
  reliefAppearance: 'RA',
};

export const shortBattingStatMessages: Record<BattingStatKey, string> = {
  runs: 'R',
  rbi: 'RBI',
  singles: '1B',
  doubles: '2B',
  triples: '3B',
  homeRuns: 'HR',
  walks: 'BB',
  strikeouts: 'K',
  stolenBases: 'SB',
  hitByPitch: 'HBP',
  stealsCaught: 'CS',
};

export const longPitchingStatMessages: Record<PitchingStatKey, string> = {
  outs: 'Innings Pitched',
  strikeouts: 'Strikeouts',
  hitsAllowed: 'Hits Allowed',
  earnedRuns: 'Earned Runs',
  walks: 'Walks',
  hitBatsmen: 'Hit Batsmen',
  win: 'Win',
  save: 'Save',
  hold: 'Hold',
  reliefAppearance: 'Relief Appearance',
};

export const longBattingStatMessages: Record<BattingStatKey, string> = {
  runs: 'Runs',
  rbi: 'RBI',
  singles: 'Singles',
  doubles: 'Doubles',
  triples: 'Triples',
  homeRuns: 'Home Runs',
  walks: 'Walks',
  strikeouts: 'Strikeouts',
  stolenBases: 'Stolen Bases',
  hitByPitch: 'Hit By Pitch',
  stealsCaught: 'Caught Stealing',
};
